import {jsPDF} from "jspdf";
import autoTable from "jspdf-autotable";
import {addCanvasToPdfPaged, captureDomSubtreeToCanvas, type SnapshotCaptureOptions} from "~/utilities/snapshotCapture";

/**
 * Builds a download filename from the visible page title (`Enquiries.csv`).
 * Strips characters that are illegal on common filesystems.
 */
export function buildPageTitleExportFilename(pageTitle: string, extension: string): string {
    const trimmedExtension = extension.startsWith(".") === true ? extension.slice(1) : extension;
    const trimmedTitle = pageTitle.trim();
    const escapedExtension = trimmedExtension.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const withoutMatchingExtension = trimmedTitle.replace(new RegExp(`\\.${escapedExtension}$`, "i"), "");
    const sanitized = withoutMatchingExtension.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "").replace(/\s+/g, " ").trim();
    const base = sanitized === "" ? "export" : sanitized;
    return `${base}.${trimmedExtension}`;
}

function downloadDataUrl(dataUrl: string, filename: string) {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename;
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
}

function cellText(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    if (typeof value === "bigint" || typeof value === "symbol" || typeof value === "function") {
        return value.toString();
    }
    if (typeof value === "object") {
        return JSON.stringify(value);
    }
    return "";
}

/**
 * Stable column order: keys from the first row, then any additional keys from later rows.
 */
function exportColumnKeys<T extends Record<string, unknown>>(rows: Array<T>): string[] {
    if (rows.length === 0) {
        return [];
    }
    const order: string[] = [];
    const seen = new Set<string>();
    for (const key of Object.keys(rows[0]!)) {
        order.push(key);
        seen.add(key);
    }
    for (let i = 1; i < rows.length; i++) {
        for (const key of Object.keys(rows[i]!)) {
            if (!seen.has(key)) {
                seen.add(key);
                order.push(key);
            }
        }
    }
    return order;
}

/**
 * Exports data rows as a PDF table.
 */
export function exportRowsAsPdf<T extends Record<string, unknown>>(
    rows: Array<T>,
    filename: string = "export.pdf",
    title: string = "Data Export",
) {
    if (rows.length === 0) {
        return;
    }

    const columns = exportColumnKeys(rows);
    const body = rows.map((row) => columns.map((col) => cellText(row[col])));

    const orientation = columns.length > 7 ? "landscape" : "portrait";
    const doc = new jsPDF({
        orientation,
        unit: "mm",
        format: "a4",
    });

    const pageInnerWidth = orientation === "landscape" ? 277 : 190;

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    const titleLines: string[] = doc.splitTextToSize(title, pageInnerWidth - 20);
    doc.text(titleLines, 14, 12);

    const titleBlockHeight = titleLines.length * 5;
    const tableStartY = 12 + titleBlockHeight + 4;

    autoTable(doc, {
        head: [columns],
        body,
        startY: tableStartY,
        theme: "grid",
        styles: {
            font: "helvetica",
            fontStyle: "normal",
            fontSize: 7,
            cellPadding: 1.5,
            overflow: "linebreak",
            valign: "top",
            cellWidth: "wrap",
            minCellWidth: 14,
        },
        headStyles: {
            fillColor: [240, 240, 240],
            textColor: 20,
            fontStyle: "bold",
            overflow: "linebreak",
            valign: "top",
        },
        bodyStyles: {
            valign: "top",
        },
        margin: {left: 10, right: 10},
        tableWidth: "auto",
        showHead: "everyPage",
        pageBreak: "auto",
        rowPageBreak: "auto",
        horizontalPageBreak: columns.length > 5,
        horizontalPageBreakBehaviour: "afterAllRows",
    });

    doc.save(filename);
}

/** Options for PNG/PDF DOM snapshot exports (see `SnapshotCaptureOptions`). */
export type ElementSnapshotExportOptions = SnapshotCaptureOptions;

/**
 * Snapshot options plus dashboard template layout. Emotorad passes
 * `useDashboardWidgetCompositeExport: false` until `feature/emotorad-dashboard` owns composite exports.
 */
export type DashboardTemplateExportOptions = ElementSnapshotExportOptions & {
    /**
     * When `false`, skips stitched per-widget PNG/PDF layout and uses one full-root capture.
     * @default true
     */
    useDashboardWidgetCompositeExport?: boolean;
};

function splitDashboardTemplateExportOptions(options: DashboardTemplateExportOptions): {
    snapshot: ElementSnapshotExportOptions;
    useComposite: boolean;
} {
    const useComposite = options.useDashboardWidgetCompositeExport !== false;
    const snapshot = {...options};
    delete snapshot.useDashboardWidgetCompositeExport;
    return {snapshot, useComposite};
}

/**
 * Exports an HTML element as a PNG image (settle → optional expand → serialize charts → html2canvas).
 */
export async function exportElementAsPng(
    element: HTMLElement,
    filename: string = "export.png",
    options: ElementSnapshotExportOptions = {},
) {
    const captureOptions: SnapshotCaptureOptions = {
        ...options,
        expandOverflowContainers: options.expandOverflowContainers === true,
    };
    const {canvas} = await captureDomSubtreeToCanvas(element, captureOptions);
    downloadDataUrl(canvas.toDataURL("image/png"), filename);
}

/**
 * Rasterizes a DOM subtree and saves a multi-page PDF (same visual as PNG export).
 */
export async function exportElementAsSnapshotPdf(
    element: HTMLElement,
    filename: string = "export.pdf",
    title: string = "Export",
    options: ElementSnapshotExportOptions = {},
) {
    const captureOptions: SnapshotCaptureOptions = {
        ...options,
        expandOverflowContainers: options.expandOverflowContainers !== false,
    };
    const {canvas, domContentHeight, sectionHeaderOffsetsDom} = await captureDomSubtreeToCanvas(
        element,
        captureOptions,
    );

    const margin = 10;
    const doc = new jsPDF({orientation: "portrait", unit: "mm", format: "a4"});
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const titleMaxW = doc.internal.pageSize.getWidth() - 2 * margin;
    const titleLines: string[] = doc.splitTextToSize(title, titleMaxW);
    doc.text(titleLines, margin, 14);
    const titleBlockMm = titleLines.length * 5 + 4;

    const useSectionPaging =
        captureOptions.sectionAnchorSelector !== null && sectionHeaderOffsetsDom.length > 0;
    addCanvasToPdfPaged(
        doc,
        canvas,
        margin,
        margin + titleBlockMm,
        useSectionPaging
            ? {
                  domContentHeight,
                  sectionHeaderOffsetsDom,
                  sectionSnapGuardPx: captureOptions.sectionSnapGuardPx,
              }
            : undefined,
    );
    doc.save(filename);
}

type CapturedDashboardWidget = {
    sectionTitle: string;
    rowTitle: string;
    widgetTitle: string;
    canvas: HTMLCanvasElement | null;
    error?: string;
};

async function captureWidgetsForDashboardTemplate(
    root: HTMLElement,
    options: ElementSnapshotExportOptions = {},
): Promise<CapturedDashboardWidget[]> {
    const sectionElements = Array.from(root.querySelectorAll<HTMLElement>("[data-export-section]"));
    const widgets: CapturedDashboardWidget[] = [];

    for (const sectionEl of sectionElements) {
        const sectionTitle = sectionEl.getAttribute("data-export-section") ?? "Section";
        const rowElements = Array.from(sectionEl.querySelectorAll<HTMLElement>("[data-export-dashboard-row]"));
        for (const rowEl of rowElements) {
            const rowTitleRaw = rowEl.getAttribute("data-export-dashboard-row") ?? "";
            const rowTitle = rowTitleRaw.trim();
            const widgetElements = Array.from(rowEl.querySelectorAll<HTMLElement>("[data-export-widget-title]"))
                .filter((widgetEl) => widgetEl.closest("[data-export-dashboard-row]") === rowEl);
            for (const widgetEl of widgetElements) {
                const widgetTitle = (widgetEl.getAttribute("data-export-widget-title") ?? "").trim();
                try {
                    const {canvas} = await captureDomSubtreeToCanvas(widgetEl, {
                        ...options,
                        expandOverflowContainers: false,
                        sectionAnchorSelector: null,
                        scaleOverride: options.scaleOverride ?? 1,
                    });
                    widgets.push({sectionTitle, rowTitle, widgetTitle, canvas});
                } catch (error) {
                    widgets.push({
                        sectionTitle,
                        rowTitle,
                        widgetTitle,
                        canvas: null,
                        error: error instanceof Error ? error.message : "Capture failed",
                    });
                }
            }
        }
    }
    return widgets;
}

export async function exportDashboardAsTemplatePng(
    root: HTMLElement,
    filename: string = "performance-dashboard.png",
    reportTitle: string = "Performance Dashboard",
    options: DashboardTemplateExportOptions = {},
) {
    const {snapshot, useComposite} = splitDashboardTemplateExportOptions(options);
    if (!useComposite) {
        await exportElementAsPng(root, filename, snapshot);
        return;
    }
    const widgets = await captureWidgetsForDashboardTemplate(root, snapshot);
    if (widgets.length === 0) {
        await exportElementAsPng(root, filename, snapshot);
        return;
    }

    const PAGE_WIDTH = 1440;
    const PAD_X = 40;
    const GAP = 20;
    const CARD_PAD = 16;
    const TITLE_H = 54;
    const SUBTITLE_H = 28;
    const ROW_H = 24;
    const IMAGE_MAX_W = PAGE_WIDTH - PAD_X * 2 - CARD_PAD * 2;

    let totalH = PAD_X + TITLE_H;
    let prevSection = "";
    let prevRow = "";
    for (const item of widgets) {
        if (item.sectionTitle !== prevSection) {
            totalH += SUBTITLE_H;
            prevSection = item.sectionTitle;
            prevRow = "";
        }
        if (item.rowTitle !== "" && item.rowTitle !== prevRow) {
            totalH += ROW_H;
            prevRow = item.rowTitle;
        }
        totalH += CARD_PAD * 2 + 24;
        if (item.canvas !== null) {
            const ratio = item.canvas.height / Math.max(item.canvas.width, 1);
            totalH += Math.max(180, IMAGE_MAX_W * ratio);
        } else {
            totalH += 120;
        }
        totalH += GAP;
    }
    totalH += PAD_X;

    const canvas = document.createElement("canvas");
    canvas.width = PAGE_WIDTH;
    canvas.height = Math.max(1, Math.ceil(totalH));
    const ctx = canvas.getContext("2d");
    if (ctx === null) {
        return;
    }

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#172b4d";
    ctx.font = "700 28px Inter, system-ui, -apple-system, sans-serif";
    ctx.fillText(reportTitle, PAD_X, PAD_X + 28);

    let y = PAD_X + TITLE_H;
    prevSection = "";
    prevRow = "";
    for (const item of widgets) {
        if (item.sectionTitle !== prevSection) {
            ctx.fillStyle = "#1d4ed8";
            ctx.font = "700 18px Inter, system-ui, -apple-system, sans-serif";
            ctx.fillText(item.sectionTitle, PAD_X, y + 18);
            y += SUBTITLE_H;
            prevSection = item.sectionTitle;
            prevRow = "";
        }
        if (item.rowTitle !== "" && item.rowTitle !== prevRow) {
            ctx.fillStyle = "#334155";
            ctx.font = "600 14px Inter, system-ui, -apple-system, sans-serif";
            ctx.fillText(item.rowTitle, PAD_X, y + 14);
            y += ROW_H;
            prevRow = item.rowTitle;
        }

        const cardX = PAD_X;
        const cardW = PAGE_WIDTH - PAD_X * 2;
        const imageH = item.canvas !== null
            ? Math.max(180, IMAGE_MAX_W * (item.canvas.height / Math.max(item.canvas.width, 1)))
            : 120;
        const cardH = CARD_PAD * 2 + 24 + imageH;

        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#d0d7de";
        ctx.lineWidth = 1;
        ctx.fillRect(cardX, y, cardW, cardH);
        ctx.strokeRect(cardX, y, cardW, cardH);

        const widgetTitle = item.widgetTitle.length > 0 ? item.widgetTitle : "Widget";
        ctx.fillStyle = "#0f172a";
        ctx.font = "600 14px Inter, system-ui, -apple-system, sans-serif";
        ctx.fillText(widgetTitle, cardX + CARD_PAD, y + CARD_PAD + 14);

        const imageX = cardX + CARD_PAD;
        const imageY = y + CARD_PAD + 24;
        const imageW = IMAGE_MAX_W;
        if (item.canvas !== null) {
            ctx.drawImage(item.canvas, imageX, imageY, imageW, imageH);
        } else {
            ctx.fillStyle = "#fff1f2";
            ctx.fillRect(imageX, imageY, imageW, imageH);
            ctx.fillStyle = "#991b1b";
            ctx.font = "500 13px Inter, system-ui, -apple-system, sans-serif";
            ctx.fillText(item.error ?? "Unable to capture widget", imageX + 12, imageY + 24);
        }

        y += cardH + GAP;
    }

    downloadDataUrl(canvas.toDataURL("image/png"), filename);
}

export async function exportDashboardAsTemplatePdf(
    root: HTMLElement,
    filename: string = "performance-dashboard.pdf",
    reportTitle: string = "Performance Dashboard",
    options: DashboardTemplateExportOptions = {},
) {
    const {snapshot, useComposite} = splitDashboardTemplateExportOptions(options);
    if (!useComposite) {
        await exportElementAsSnapshotPdf(root, filename, reportTitle, snapshot);
        return;
    }
    const widgets = await captureWidgetsForDashboardTemplate(root, snapshot);
    if (widgets.length === 0) {
        await exportElementAsSnapshotPdf(root, filename, reportTitle, snapshot);
        return;
    }

    const doc = new jsPDF({orientation: "portrait", unit: "mm", format: "a4"});
    const margin = 10;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const contentW = pageW - margin * 2;

    let y = margin + 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(reportTitle, margin, y);
    y += 8;

    let prevSection = "";
    let prevRow = "";
    for (const item of widgets) {
        const ensureSpace = (neededMm: number) => {
            if (y + neededMm <= pageH - margin) {
                return;
            }
            doc.addPage();
            y = margin + 8;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text(reportTitle, margin, y);
            y += 8;
            prevSection = "";
            prevRow = "";
        };

        if (item.sectionTitle !== prevSection) {
            ensureSpace(10);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.text(item.sectionTitle, margin, y);
            y += 6;
            prevSection = item.sectionTitle;
            prevRow = "";
        }
        if (item.rowTitle !== "" && item.rowTitle !== prevRow) {
            ensureSpace(7);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(item.rowTitle, margin, y);
            y += 5;
            prevRow = item.rowTitle;
        }

        const cardPad = 3;
        const titleMm = 6;
        const widgetTitle = item.widgetTitle.length > 0 ? item.widgetTitle : "Widget";
        let imageH = 45;
        if (item.canvas !== null) {
            imageH = Math.max(40, (item.canvas.height / Math.max(item.canvas.width, 1)) * (contentW - cardPad * 2));
        }
        const blockH = cardPad * 2 + titleMm + imageH + 2;
        ensureSpace(blockH + 4);

        doc.setDrawColor(208, 215, 222);
        doc.rect(margin, y, contentW, blockH);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(widgetTitle, margin + cardPad, y + cardPad + 3.5);

        const imgX = margin + cardPad;
        const imgY = y + cardPad + titleMm;
        const imgW = contentW - cardPad * 2;
        if (item.canvas !== null) {
            doc.addImage(item.canvas.toDataURL("image/png"), "PNG", imgX, imgY, imgW, imageH);
        } else {
            doc.setTextColor(153, 27, 27);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.text(item.error ?? "Unable to capture widget", imgX + 1, imgY + 4);
            doc.setTextColor(0, 0, 0);
        }
        y += blockH + 4;
    }

    doc.save(filename);
}

/**
 * Reads `data-export-widget-*` attributes from the dashboard export root for CSV rows.
 */
export type DashboardWidgetCsvRow = {
    Section: string;
    Row: string;
    Title: string;
    Kind: string;
    Value: string;
};

export function buildDashboardWidgetRowsFromDom(root: HTMLElement): DashboardWidgetCsvRow[] {
    const rows: DashboardWidgetCsvRow[] = [];
    const nodes = root.querySelectorAll("[data-export-widget-title]");
    nodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
            return;
        }
        const sectionEl = node.closest("[data-export-section]");
        const section =
            sectionEl instanceof HTMLElement
                ? (sectionEl.getAttribute("data-export-section") ?? "")
                : "";
        const title = node.getAttribute("data-export-widget-title") ?? "";
        const kind = node.getAttribute("data-export-widget-kind") ?? "";
        const value = node.getAttribute("data-export-widget-value") ?? "";
        const rowTitleEl = node.closest("[data-export-dashboard-row]");
        const rowTitle =
            rowTitleEl instanceof HTMLElement
                ? (rowTitleEl.getAttribute("data-export-dashboard-row") ?? "")
                : "";
        rows.push({
            Section: section,
            Row: rowTitle,
            Title: title,
            Kind: kind,
            Value: value.length > 0 ? value : "(chart — see PNG/PDF)",
        });
    });
    return rows;
}

const PNG_TABLE_CELL_STYLE = [
    "border: 1px solid #ccc",
    "padding: 8px 10px",
    "vertical-align: top",
    "text-align: left",
    "white-space: pre-wrap",
    "word-break: break-word",
    "overflow-wrap: anywhere",
    "max-width: 28rem",
    "font-size: 12px",
    "line-height: 1.35",
    "color: #172b4d",
].join("; ");

const PNG_TABLE_HEAD_STYLE = [
    PNG_TABLE_CELL_STYLE,
    "background-color: #f4f5f7",
    "font-weight: 600",
].join("; ");

/**
 * "Fake" PNG export from data (creates a temporary table and snapshots it)
 */
export async function exportRowsAsPng<T extends Record<string, unknown>>(
    rows: Array<T>,
    filename: string = "export.png",
    title: string = "Data Export",
) {
    if (rows.length === 0) {
        return;
    }

    const columns = exportColumnKeys(rows);
    const container = document.createElement("div");
    container.style.cssText = [
        "position: fixed",
        "left: -99999px",
        "top: 0",
        "width: max-content",
        "max-width: none",
        "overflow: visible",
        "box-sizing: border-box",
        "background: #ffffff",
        "padding: 24px",
        "pointer-events: none",
        "font-family: system-ui, -apple-system, 'Segoe UI', sans-serif",
    ].join("; ");

    const heading = document.createElement("h2");
    heading.textContent = title;
    heading.style.cssText = "margin: 0 0 16px 0; font-size: 18px; font-weight: 600; color: #172b4d";
    container.appendChild(heading);

    const table = document.createElement("table");
    table.style.cssText = [
        "border-collapse: collapse",
        "width: max-content",
        "table-layout: auto",
    ].join("; ");

    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    columns.forEach((col) => {
        const th = document.createElement("th");
        th.textContent = col;
        th.style.cssText = PNG_TABLE_HEAD_STYLE;
        headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    rows.forEach((row) => {
        const tr = document.createElement("tr");
        columns.forEach((col) => {
            const td = document.createElement("td");
            td.textContent = cellText(row[col]);
            td.style.cssText = PNG_TABLE_CELL_STYLE;
            tr.appendChild(td);
        });
        tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    container.appendChild(table);

    document.body.appendChild(container);
    await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
    try {
        await exportElementAsPng(container, filename);
    } finally {
        document.body.removeChild(container);
    }
}

function escapeCsvCell(raw: string): string {
    if (raw.includes(",") || raw.includes("\n") || raw.includes('"')) {
        return `"${raw.replace(/"/g, '""')}"`;
    }
    return raw;
}

/**
 * Downloads arbitrary row objects as CSV (RFC-style quoting for commas/quotes/newlines).
 */
export function downloadObjectRowsAsCsv(
    rows: Array<Record<string, unknown>>,
    filename: string = "export.csv",
) {
    if (rows.length === 0) {
        return;
    }

    const columns = exportColumnKeys(rows);
    const header = columns.map(escapeCsvCell).join(",");
    const body = rows
        .map((row) => columns.map((col) => escapeCsvCell(cellText(row[col]))).join(","))
        .join("\n");
    const csv = `${header}\n${body}`;
    const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
